// Result list visuals extracted from add-asset-search-results.tsx
// to keep that file under the max-lines policy. Renders the grouped
// System → SubSystem → AssetType tree plus the chevron-styled rows.
// Purely presentational — selection lives in the parent.

import type { ReactElement } from 'react'

import type { AssetTypeSearchRow } from '../../hooks/use-classification'
import { Spinner } from '../../ui/spinner'

import type { SystemGroup } from './add-asset-search-results-types'

interface ResultGroupsProps {
  groups: SystemGroup[]
  creatingAssetTypeId: string | null
  onSelectExisting: (row: AssetTypeSearchRow) => void
}

export function ResultGroups({
  groups,
  creatingAssetTypeId,
  onSelectExisting,
}: ResultGroupsProps): ReactElement {
  return (
    <ul className="flex flex-col gap-5 py-2">
      {groups.map((system) => (
        <li key={system.systemId}>
          <SystemHeader
            name={system.systemName}
            count={system.subSystems.reduce(
              (n, s) => n + s.types.length,
              0,
            )}
          />
          <ul className="mt-2 flex flex-col gap-3">
            {system.subSystems.map((sub) => (
              <li key={sub.subSystemId}>
                <SubSystemLabel
                  name={sub.subSystemName}
                  count={sub.types.length}
                />
                <ul className="mt-1.5 flex flex-col gap-1">
                  {sub.types.map((row) => {
                    const isCreating = creatingAssetTypeId === row.id
                    return (
                      <li key={row.id}>
                        <ResultRow
                          row={row}
                          disabled={creatingAssetTypeId !== null}
                          isCreating={isCreating}
                          onSelect={() => onSelectExisting(row)}
                        />
                      </li>
                    )
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}

interface SystemHeaderProps {
  name: string
  count: number
}

function SystemHeader({ name, count }: SystemHeaderProps): ReactElement {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-3 bg-white pt-2 pb-1">
      <h3 className="font-display text-lg leading-none text-cc-forest">
        {name}
      </h3>
      <span className="ml-auto text-[11px] text-cc-forest/50">{count}</span>
    </div>
  )
}

interface SubSystemLabelProps {
  name: string
  count: number
}

// "Line" variant: small uppercase tracked label, hairline divider
// filling the remaining space, trailing count.
function SubSystemLabel({ name, count }: SubSystemLabelProps): ReactElement {
  return (
    <div className="flex items-center gap-2.5 text-cc-forest/55">
      <h4 className="text-[11px] font-medium uppercase tracking-[0.14em]">
        {name}
      </h4>
      <span aria-hidden="true" className="h-px flex-1 bg-cc-forest/10" />
      <span className="text-[10px]">{count}</span>
    </div>
  )
}

interface ResultRowProps {
  row: AssetTypeSearchRow
  disabled: boolean
  isCreating: boolean
  onSelect: () => void
}

function ResultRow({
  row,
  disabled,
  isCreating,
  onSelect,
}: ResultRowProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-busy={isCreating || undefined}
      className="group flex min-h-11 w-full items-center gap-3 rounded-xl border border-cc-forest/10 bg-white px-3 py-2.5 text-left transition-colors hover:border-cc-forest hover:bg-cc-cream/50 focus-visible:border-cc-forest focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cc-lime disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-cc-forest">
          {row.name}
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          <span className="text-[11px] text-cc-forest/55">
            {row.unitOfMeasure}
          </span>
          {row.isFieldAdded ? (
            <span className="rounded bg-cc-lime/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-cc-forest">
              Field added
            </span>
          ) : null}
        </span>
      </span>
      {isCreating ? (
        <Spinner label="Adding" size="sm" />
      ) : (
        <span
          aria-hidden="true"
          className="text-cc-forest/30 transition-colors group-hover:text-cc-forest"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 3 L11 8 L6 13"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </button>
  )
}
